import type { FormEvent } from 'react';
import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { HBox } from '../layout';
import { Button, Icon } from '../basic';
import {
  Breakpoints,
  intl,
  intlFmtTemplateString,
  useBreakpoint,
  useContentEditable
} from '../../lib';
import { EmojiPickerComp } from './EmojiPickerComp';
import { useLayer } from 'react-laag';
import { DEFAULT_PLACEMENT_CONFIG } from '../../util';
import type { PlacementType } from 'react-laag/dist/PlacementType';
import { FocusTrap } from 'focus-trap-react';
import type { EmojiClickData } from 'emoji-picker-react';
import {
  scaledEmojiNodes,
  scaledEmojiSpan
} from './ChatMessageComponents/scaledEmoji';
import type { EmojiData } from 'emoji-picker-react/src/types/exposedTypes';

/**
 * Props for a {@link ChatInput} component.
 */
export interface ChatInputProps {
  /**
   * Handler for when chat message is submitted.
   * @param message message typed in the text input.
   */
  onMessageSubmit: (message: string) => Promise<unknown>;
  showAttachments: boolean;
  showEmojis: boolean;
  disabled?: boolean;
  maxTextLength?: number;
  /** Receives the editable element, e.g. to focus it from the outside. */
  inputRef?: React.RefObject<HTMLElement | null>;
  /**
   * Text to prefill the input.
   */
  initialText?: string;
  /**
   * Called with the current text on every change.
   * Intended for persisting a draft of the not-yet-submitted message.
   */
  onTextChange?: (text: string) => void;
  'data-testid'?: string;
  emojiData?: EmojiData;
}

/**
 * Basic Chat input element.
 *
 * It consists of {@link TextInput} and {@link Button}. When submit is triggered, provided  onMessageSubmit function is fired.

 * @constructor
 */
export function ChatInput({
  onMessageSubmit,
  showAttachments = true,
  showEmojis = true,
  disabled = false,
  maxTextLength = 10000,
  inputRef,
  initialText,
  onTextChange,
  emojiData = { categories: {}, emojis: {} },
  ...props
}: Readonly<ChatInputProps>): React.JSX.Element {
  const [classes, setClasses] = useState(['ehs-chat-input']);
  const [errorMessage, setErrorMessage] = useState('');
  const errorId = useId();
  const labelId = useId();
  const testId = props['data-testid'];
  const smallScreen = useBreakpoint(Breakpoints.SMALL);
  const isMobile = useBreakpoint(Breakpoints.MOBILE);
  const isBigScreen = useBreakpoint(Breakpoints.MEDIUMBIG);

  const handleChange = useCallback(
    (text: string, element: HTMLElement) => {
      setErrorMessage(
        text.length > maxTextLength
          ? intlFmtTemplateString('react.field.string.max', {
              max: maxTextLength.toString()
            })
          : ''
      );
      setClasses(current => {
        const next = current.filter(c => c !== 'error' && c !== 'extended');
        if (text.length > maxTextLength) next.push('error');
        if (
          (element.scrollHeight > 43 ||
            (isMobile && window.innerWidth - element.clientWidth < 100) ||
            (!isMobile &&
              isBigScreen &&
              window.innerWidth - element.clientWidth < 510) ||
            (!isMobile && !isBigScreen && element.innerText.length > 100)) &&
          element.innerText.length > 20
        )
          next.push('extended');
        return next;
      });
      onTextChange?.(text);
    },
    [maxTextLength, isMobile, isBigScreen, onTextChange]
  );

  const editor = useContentEditable({
    toNodes: scaledEmojiNodes,
    onChange: handleChange,
    ref: inputRef
  });
  const message = editor.text;

  // Prefill once on mount, e.g. to restore a draft.
  useEffect(() => {
    if (initialText) {
      editor.insert(initialText, { focus: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onMessageSubmit(message);
    editor.clear();
    editor.focus();
    setClasses(classes.filter(c => c !== 'extended'));
    setOpen(false);
  };

  const addEmoji = (emoji: EmojiClickData) => {
    const name = emoji.names?.[emoji.names.length - 1];
    /*
     * Focus stays out for now: the picker's focus trap is still mounted and
     * would pull it straight back. It is handed to the input once the picker
     * has closed, see the effect below.
     */
    editor.insertNodes([scaledEmojiSpan(emoji.emoji, emojiData, name)], {
      focus: false
    });
    if (!isMobile) {
      focusAfterClose.current = true;
      setOpen(false);
    }
  };

  function getEmojiPickerPlacement(): PlacementType {
    if (isMobile) return 'bottom-start';
    return smallScreen ? 'top-start' : 'top-end';
  }

  const [isOpen, setOpen] = useState(false);
  const focusAfterClose = useRef(false);

  useEffect(() => {
    if (isOpen || !focusAfterClose.current) return;
    focusAfterClose.current = false;
    editor.focus();
  }, [editor, isOpen]);

  /*
   * On mobile the picker is part of the normal document flow below the input,
   * so react-laag must stay idle: it would otherwise apply absolute positioning
   * and register an outside-click handler for an element it does not own.
   */
  const useFloatingPicker = !isMobile;
  const { layerProps, triggerProps, renderLayer } = useLayer({
    isOpen: isOpen && useFloatingPicker,
    onOutsideClick: () => setOpen(false),
    ...DEFAULT_PLACEMENT_CONFIG,
    placement: getEmojiPickerPlacement()
  });
  const layer = renderLayer(
    <FocusTrap
      focusTrapOptions={{
        allowOutsideClick: true,
        escapeDeactivates: () => {
          setOpen(false);
          editor.focus();
          return true;
        },
        /*
         * Closing the picker must not pull the focus back to the emoji button:
         * the caret belongs behind the freshly inserted emoji in the input.
         */
        returnFocusOnDeactivate: false,
        preventScroll: true,
        initialFocus: false
      }}
    >
      <div {...layerProps}>
        <EmojiPickerComp
          addMessage={addEmoji}
          emojiData={emojiData}
          showSearch={false}
          data-testid={testId + '-emoji-picker'}
        />
      </div>
    </FocusTrap>
  );

  const sendMessageForbidden = message.trim() === '' || errorMessage != '';

  /*
   * Plain JSX instead of a nested component: a component declared inside the
   * render function is a new type on every render, which would remount the
   * emoji button and detach the layer trigger ref with it.
   */
  const innerButtons = (
    <>
      {showAttachments && (
        <Button
          variant="tertiary"
          className="attachment"
          text={intl('share.lbAttachment')}
          icon="attachment"
          scale="small"
          data-testid={testId && testId + '-attachment-button'}
        />
      )}
      {showEmojis && (
        <Button
          {...triggerProps}
          variant="tertiary"
          className="emoji"
          text={
            isMobile && isOpen
              ? intl('matrixChatPortlet.input.openKeyboard')
              : intl('common.addEmoji')
          }
          icon={isMobile && isOpen ? 'keyboard' : 'emoji'}
          scale="small"
          data-testid={testId && testId + '-emoji-button'}
          onClick={
            isOpen
              ? () => {
                  setOpen(false);
                  editor.focus();
                }
              : () => setOpen(true)
          }
          disabled={disabled}
          aria-disabled={disabled}
        />
      )}
      {(showEmojis || showAttachments) && (
        <div className="button-divider"></div>
      )}
      <Button
        className="submit"
        icon="send_message"
        text={intl('common.send.message')}
        variant="tertiary"
        type="submit"
        disabled={sendMessageForbidden}
        scale="small"
        data-testid={testId && testId + '-submit-button'}
      />
    </>
  );
  const buttons =
    smallScreen || isMobile ? (
      innerButtons
    ) : (
      <HBox className="button-container">{innerButtons}</HBox>
    );

  return (
    <form
      {...props}
      className={classes.join(' ')}
      onSubmit={handleSubmit}
      data-testid={testId}
    >
      {isOpen && showEmojis && useFloatingPicker && layer}
      <HBox align="stretch" className="textfield-container">
        <label id={labelId} className="sr-only">
          {intl('common.chatmessage')}
        </label>
        <div
          {...editor.editableProps}
          contentEditable={!disabled}
          role="textbox"
          aria-multiline="true"
          aria-labelledby={labelId}
          aria-disabled={disabled || undefined}
          data-placeholder={intl('common.messaging.input.label')}
          data-testid={testId + '-message-input'}
          className="textfield-input"
          onFocus={() => {
            // On mobile the picker replaces the keyboard, so typing closes it.
            if (isMobile) setOpen(false);
          }}
          onKeyDown={event => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              if (!sendMessageForbidden) {
                event.currentTarget.closest('form')?.requestSubmit();
              }
            }
          }}
          aria-describedby={errorMessage ? errorId : undefined}
        />
        {buttons}
      </HBox>
      {isOpen && showEmojis && !useFloatingPicker && (
        <div
          className="emoji-picker-inline"
          data-testid={testId && testId + '-emoji-picker'}
          onKeyDown={event => {
            if (event.key === 'Escape') {
              setOpen(false);
            }
          }}
        >
          <EmojiPickerComp
            addMessage={addEmoji}
            emojiData={emojiData}
            showSearch={false}
            height="100%"
          />
        </div>
      )}
      {errorMessage && (
        <HBox id={errorId} className="error-message">
          <Icon iconName="shui_error" aria-label={undefined} />
          {errorMessage}
        </HBox>
      )}
    </form>
  );
}
